import { useContext } from 'react'

import { httpsCallable } from 'firebase/functions'
import { useHistory } from 'react-router'

// Helpers
import useToasterHelper from '../helpers/ToasterHelper'
import { getConvertedData, getSelectedUserAsTeamMember } from '../models/interface.helper'
import { authHelpers, getUserFullName } from '../helpers/helpers'
import { uploadPicture } from '../helpers/uploadPicture'
import { createHorseData } from '../components/modals/common/createHorseData'

// Firebase
import { createUserWithEmailAndPassword, getAuth, updateProfile } from 'firebase/auth'

// Services
import FirebaseApp from '../services/firebaseApp'
import { httpService } from '../services/httpService'
import UserService from '../services/firebase-service/users/userService'
import FirestoreService from '../services/firestoreService'
import { HandleModalContext } from '../layout/mainlayout/MainLayout'

// Store
import { setIsLoggedIn, setProfileDetails, setUserId, storeUserId } from '../store/user/userSlice'
import {
  IHorseProfileRegistration,
  riderProfileRegistrationActions,
  selectRiderProfileRider,
} from '../store/riderProfileRegistration/riderProfileRegistrationSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { setHorses, setIsMyHorse, setSelectedHorseStatus } from '../store/horses/horseSlice'
import { storeUseMode } from '../store/system/systemThunk'
import { selectTeamTabData, setTeamTabData } from '../store/registration/registrationSlice'

// Models
import { UserModel } from '../models/users/user.model'
import { ModelBaseModel } from '../models/model-base/model-base.model'

// Types
import { IRiderTeamMember, IUserInterface } from '../models/users/user.interface'
import { IRiderSignupForm } from '../types/competitor_types'
import { ITeamMemberR } from '../models/event-registered-users/event-registered-users.interface'

// Const
import { MODAL_CONSTS } from '../const/modal-const'
import { CONST, USER_DEF_PASS } from '../const/const'
import FIREBASE_CONST from '../const/firebase-const'
import { MESSAGES_CONST } from '../const/messages-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS
const HORSE_PATH = CONST.DATA.STORAGE.HORSE.HORSE_PROFILE_IMAGE_URL.PREFIX
const USER_PATH = CONST.DATA.STORAGE.USERS.COMPETITOR_PROFILE_IMAGE_URL.PREFIX

const auth = getAuth()

type ISubmitOnlyMemberHandler = {
  competitor: IUserInterface
  teamMember: {
    email: string
    userName: string
    userFullName: string
    date: string
    nationality: string
    discipline: string
    picture?: File | null
  }
}

export const useCreate = () => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()
  const history = useHistory()

  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal

  const riderProfileRider = useAppSelector(selectRiderProfileRider)
  const teamTabData = useAppSelector(selectTeamTabData)

  const workflowNotifyAccountCreatedForYou = async (
    userId: string,
    userEmail: string,
    userFullName: string,
    competitorId: string
  ) => {
    try {
      const notifyMessageFunc = httpsCallable(FirebaseApp.functions, 'triggerWorkflow')

      await notifyMessageFunc({
        workflowKey: 'account-created-for-you',
        recipients: [
          {
            id: userId,
            name: userFullName,
            email: userEmail,
          },
        ],
        data: {
          defpass: USER_DEF_PASS,
        },
        actor: competitorId,
      })
    } catch (error) {
      console.error('error', error)
    }
  }

  const updateUserTeamMembers = async (team: IUserInterface[], competitor: IUserInterface) => {
    let updatedTeamMembersList: IRiderTeamMember[] = team.map((user) => ({
      memberId: user.id,
      memberDob: user.userDOB || null,
      memberName: getUserFullName(user),
      memberEmail: user.userEmail,
      memberProfilePicture: user.userProfilePicture || '',
      connectedDate: new Date().toString(),
      memberRole: CONST.UI.TEAM_MEMBERS_ROLES.TEAM_MEMBER,
      defaultRole: user?.userDefaultRole ?? CONST.UI.TEAM_MEMBERS_ROLES.OWNER,
      selected: true,
    }))

    await FirestoreService.updateItem(COLLECTIONS.USERS.NAME, competitor.id, {
      userTeamMembers: [...(competitor.userTeamMembers ?? []), ...(updatedTeamMembersList ?? [])],
    })
  }

  const createNewCompetitor = async ({
    userEmail,
    userName,
    userFullName,
    userPassword,
    userPhoneNumber,
    userNationality,
    userDiscipline,
    userDOB,
    userPicture,
  }: {
    userEmail: string
    userName: string
    userFullName: string
    userPassword: string
    userPhoneNumber: string
    userNationality: string
    userDiscipline: string
    userDOB: string
    userPicture?: File | null
  }) => {
    try {
      const createUserResult = await createUserWithEmailAndPassword(
        auth,
        userEmail.toLowerCase(),
        userPassword
      )

      await updateProfile(createUserResult.user, {
        displayName: userFullName,
      })

      const respUserInfo = await httpService({
        url: `create_user`,
        method: 'POST',
        data: {
          uuid: createUserResult.user.uid,
          userName,
          email: userEmail.toLowerCase(),
          userType: 'competitor',
        },
      })
      const respUser = respUserInfo?.data?.user

      if (!respUser) {
        toastFunctions.error({
          message: `Failed to create user with email ${userEmail}`,
        })
        return
      }

      let downloadUrl = ''
      if (userPicture) {
        downloadUrl = (await uploadPicture(userPicture, USER_PATH)) ?? ''
      } else if (riderProfileRider?.picture) {
        downloadUrl = (await uploadPicture(riderProfileRider?.picture, USER_PATH)) ?? ''
      }

      const updatedData = {
        userProfilePicture: downloadUrl,
        userFullName,
        userName,
        userDOB,
        userNationality: userNationality || '',
        userDiscipline: userDiscipline || '',
        userPhoneNumber: userPhoneNumber || '',
      }
      await FirestoreService.updateItem(COLLECTIONS.USERS.NAME, respUser.id, {
        ...updatedData,
      })

      const user_data_doc = await UserService.getUserInfoById(createUserResult.user.uid)

      const userData = UserModel.fromFirestoreDoc(user_data_doc).toObject()

      if (!userData.userStripeId) {
        const customerData = await httpService({
          url: `create_stripe_customer`,
          method: 'POST',
          data: {
            userEmail,
            userStripeAccountId: userData.userStripeAccountId,
          },
        })

        userData.userStripeId = customerData?.customer?.stripe?.id
      }

      // commented because API not works
      // try {
      //   await httpService({
      //     url: 'create_rewardful_affiliate',
      //     method: 'POST',
      //     data: {
      //       email: userEmail,
      //       first_name: userFullName.split(' ')[0],
      //       last_name: userFullName.split(' ')[1] || '',
      //       stripe_customer_id: userData.userStripeId,
      //     },
      //   })
      // } catch (e) {
      //   console.error(e)
      // }

      return {
        ...userData,
        ...updatedData,
        userStripeId: userData.userStripeId,
      }
    } catch (error: any) {
      toastFunctions.error({
        message: error.message,
      })
      console.error(error)
    }
  }

  const createNewTeamMember = async ({ competitor, teamMember }: ISubmitOnlyMemberHandler) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      teamMember.email.toLowerCase(),
      USER_DEF_PASS
    )

    const newUser = userCredential.user

    let downloadUrl = ''
    if (teamMember.picture) {
      downloadUrl = (await uploadPicture(teamMember.picture, USER_PATH)) ?? ''
    }

    const dataToCreate = new UserModel({
      id: newUser.uid,
      userName: teamMember.userName,
      userFullName: teamMember.userFullName,
      userEmail: teamMember.email.toLowerCase(),
      userType: 'competitor',
      userProfilePicture: downloadUrl,
      userDOB: teamMember.date.toString(),
      userNationality: teamMember.nationality,
      userDiscipline: teamMember.discipline,
      userCreated: new Date(),
      userModified: new Date(),
    })

    await FirestoreService.createItemWithCustomId(COLLECTIONS.USERS.NAME, newUser.uid, {
      ...dataToCreate.toFirestore(),
      userNameNGram: new ModelBaseModel().calculateUsernameNGrams(getUserFullName(dataToCreate)),
    })

    await workflowNotifyAccountCreatedForYou(
      newUser.uid,
      teamMember.email.toLowerCase(),
      teamMember.userFullName,
      competitor.id
    )

    return {
      email: teamMember.email.toLowerCase(),
      id: newUser.uid || null,
    }
  }

  const createRiderProfile = async ({
    riderProfileRider,
    riderProfileHorse,
    riderProfileTeam,
    riderProfileNewTeam,
    riderProfileEventId,
    imageFile,
  }: {
    riderProfileRider?: IRiderSignupForm | null
    riderProfileHorse?: IHorseProfileRegistration | null
    riderProfileTeam?: IUserInterface[]
    riderProfileNewTeam?: ISubmitOnlyMemberHandler['teamMember'] | null
    riderProfileEventId?: string | null
    imageFile?: File | null
  }) => {
    if (!riderProfileRider?.fullName.split(' ')[1]) {
      toastFunctions.error({
        message: 'The full name should start with the first name, followed by the last name.',
      })
      return null
    }

    const responseError = await authHelpers.isUsernameUnique(riderProfileRider?.userName)
    if (responseError) {
      toastFunctions.error({ message: responseError })
      return null
    }

    const competitor = await createNewCompetitor({
      userEmail: riderProfileRider?.email,
      userName: riderProfileRider?.userName,
      userFullName: riderProfileRider?.fullName,
      userPassword: riderProfileRider?.password,
      userPhoneNumber: riderProfileRider?.userPhoneNumber,
      userNationality: riderProfileRider?.userNationality,
      userDiscipline: riderProfileRider?.userDiscipline,
      userDOB: riderProfileRider?.userDOB,
      userPicture: imageFile || null,
    })

    if (competitor?.id) {
      // Create Horse
      if (riderProfileHorse) {
        await createHorse({ riderProfileHorse, competitor })
      }

      // Create and add new team member user
      const userTeamMembers: IUserInterface[] = []
      if (riderProfileNewTeam) {
        const userData = await createNewTeamMember({
          competitor,
          teamMember: riderProfileNewTeam,
        })
        const userSnaps = await FirestoreService.getItem(COLLECTIONS.USERS.NAME, userData.id ?? '')
        const user = UserModel.fromFirestoreDoc(userSnaps).toObject()
        userTeamMembers.push(user)
      }

      if (riderProfileTeam) {
        userTeamMembers.push(...riderProfileTeam)
      }

      // Update user team members
      await updateUserTeamMembers(userTeamMembers, competitor)

      // Update users "Start" tab for registration on event
      const teamData: ITeamMemberR[] = []
      userTeamMembers?.forEach((member) => {
        teamData.push(getConvertedData(getSelectedUserAsTeamMember(member, competitor)))
      })
      dispatch(setTeamTabData([...teamTabData, ...teamData]))

      const updatedCompetitorSnaps = await FirestoreService.getItem(
        COLLECTIONS.USERS.NAME,
        competitor.id
      )
      const updatedCompetitor = UserModel.fromFirestoreDoc(updatedCompetitorSnaps).toObject()
      dispatch(setProfileDetails(updatedCompetitor))

      dispatch(storeUseMode(CONST.USE_MODE.COMPETITOR))
      dispatch(storeUserId(competitor.id))
      dispatch(setIsLoggedIn(true))
      dispatch(setUserId(competitor.id))

      const redirectUrl =
        riderProfileEventId && competitor
          ? `${CONST.ROUTES.REGISTER_EVENT.URL}/${riderProfileEventId}/${competitor.id}`
          : `${CONST.ROUTES.HOME.URL}`

      history.replace(redirectUrl, {
        direction: 'none',
      })

      dispatch(riderProfileRegistrationActions.clear())
      handleModal?.(false, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
      handleModal?.(false, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
      setTimeout(() => handleModal?.(true, MODAL_CONSTS.USER_CARD), 500)
    }
  }

  const createHorse = async ({
    riderProfileHorse,
    competitor,
  }: {
    riderProfileHorse: IHorseProfileRegistration
    competitor: IUserInterface
  }) => {
    try {
      const { horsePicture, inputData } = riderProfileHorse
      let downloadUrl = ''
      if (horsePicture) {
        downloadUrl = (await uploadPicture(horsePicture, HORSE_PATH)) ?? ''
      }
      const { horseToCreate } = await createHorseData({
        userData: competitor,
        horsePicture: downloadUrl,
        inputData,
      })

      dispatch(setHorses([horseToCreate]))
      dispatch(setIsMyHorse(true))
      dispatch(setSelectedHorseStatus('fulfilled'))
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: 'Horse not created' })
    }
  }

  const getFirebaseErrors = (error: any) => {
    let message = error?.code || error?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG

    if (error.code === FIREBASE_CONST.USER_NOT_FOUND) {
      message = MESSAGES_CONST.USER_NOT_FOUND
    } else if (error.code === FIREBASE_CONST.INVALID_CREDENTIAL) {
      message = 'Credentials are incorrect'
    } else if (error.code === FIREBASE_CONST.WRONG_PASSWORD) {
      message = 'Invalid password'
    } else if (error.code === FIREBASE_CONST.TOO_MANY_REQUESTS) {
      message = 'Firebase login Maximum attempts reached'
    } else if (error.code === FIREBASE_CONST.INVALID_EMAIL) {
      message = 'Invalid email address or username'
    } else if (error.code === FIREBASE_CONST.EMAIL_ALREADY_EXISTS) {
      message = MESSAGES_CONST.USER_ALREADY_EXISTS_WITH_EMAIL
    }
    return message
  }

  return {
    createNewCompetitor,
    createNewTeamMember,
    createRiderProfile,
    createHorse,
    getFirebaseErrors,
    updateUserTeamMembers,
  }
}
